import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Headless engine para FormSelect variant="menu".
 * - open/close + índice activo para navegación por teclado
 * - teclado: ↑/↓ mueve activo, Enter selecciona, Esc cierra, Home/End
 * - click-fuera (solo popover desktop; el sheet móvil usa su propio backdrop)
 * - cómputo de posición fixed para el popover desktop (flip arriba/abajo)
 *
 * `popover` = true en desktop (activa click-fuera + posicionamiento). En móvil
 * la presentación es BottomSheet, que gestiona su propio cierre.
 */
export default function useOptionsMenu({ optionCount, onSelect, popover = false }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const openMenu = useCallback((initialIndex = 0) => {
    setActiveIndex(initialIndex);
    setOpen(true);
  }, []);

  // Click-fuera — solo popover desktop
  useEffect(() => {
    if (!open || !popover) return;
    const onPointer = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener("pointerdown", onPointer, true);
    return () => document.removeEventListener("pointerdown", onPointer, true);
  }, [open, popover, close]);

  // Posición del popover desktop (portal + position:fixed, no se clipa)
  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const below = spaceBelow >= spaceAbove || spaceBelow > 280;
    const avail = (below ? spaceBelow : spaceAbove) - 12;
    setPosition({
      left: Math.round(r.left),
      width: Math.round(r.width),
      top: below ? Math.round(r.bottom + 6) : undefined,
      bottom: below ? undefined : Math.round(vh - r.top + 6),
      maxHeight: Math.max(120, Math.min(320, avail)),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open || !popover) return;
    computePosition();
    const onChange = () => computePosition();
    window.addEventListener("scroll", onChange, true);
    window.addEventListener("resize", onChange);
    return () => {
      window.removeEventListener("scroll", onChange, true);
      window.removeEventListener("resize", onChange);
    };
  }, [open, popover, computePosition]);

  const onKeyDown = useCallback(
    (e) => {
      if (!open) {
        if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openMenu(0);
        }
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((i) => Math.min(optionCount - 1, i + 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
          break;
        case "Home":
          e.preventDefault();
          setActiveIndex(0);
          break;
        case "End":
          e.preventDefault();
          setActiveIndex(optionCount - 1);
          break;
        case "Enter":
          e.preventDefault();
          if (activeIndex >= 0) onSelect(activeIndex);
          break;
        case "Escape":
          e.preventDefault();
          close();
          triggerRef.current?.focus();
          break;
        default:
          break;
      }
    },
    [open, optionCount, activeIndex, onSelect, openMenu, close]
  );

  return {
    open,
    openMenu,
    close,
    activeIndex,
    setActiveIndex,
    triggerRef,
    panelRef,
    position,
    onKeyDown,
  };
}
